import { supabase } from './supabase';

/**
 * What checkout shows until a product has its own agreement saved.
 * The product editor starts with this text so staff can rewrite it.
 */
export function defaultRentalAgreement(businessName?: string | null): string {
  const owner = (businessName || '').trim() || 'the store';
  return [
    'The first period is charged to your card with this order.',
    'Your card is kept securely on file (Stripe) and billed automatically each period until you cancel.',
    'Cancel anytime from your account — the rental runs to the end of the paid period, with no further charges.',
    `Rented equipment remains the property of ${owner}.`,
  ].join('\n');
}

/** Saved product text, or the default when the product has none yet. */
export function rentalAgreementText(saved: string | null | undefined, businessName?: string | null): string {
  const custom = (saved || '').trim();
  return custom || defaultRentalAgreement(businessName);
}

/** One recurring rental agreement (a customer renting N units of one item). */
export interface RentalAgreement {
  id: string;
  business_id: string;
  customer_id: string;
  stock_item_id: string;
  order_id: string | null;
  /** Human-friendly per-business id, e.g. R-0007 (shown in UI + on invoices). */
  rental_number: string | null;
  quantity: number;
  returned_quantity: number;
  rate_cents: number;
  billing_interval: 'month' | 'week';
  status: 'pending' | 'active' | 'past_due' | 'canceled' | 'ended';
  cancel_at_period_end: boolean;
  started_at: string | null;
  next_bill_date: string | null;
  last_billed_at: string | null;
  last_invoice_id: string | null;
  failed_attempts: number;
  ended_at: string | null;
  end_reason: string | null;
  created_at: string;
  customer?: { id: string; first_name: string | null; last_name: string | null; email: string | null; company?: string | null } | null;
  item?: { id: string; name: string; unit: string | null; deposit_cents?: number | null } | null;
}

export interface RentalEvent {
  id: string;
  agreement_id: string;
  event_type: string;
  message: string;
  details: Record<string, unknown> | null;
  actor_id: string | null;
  created_at: string;
}

const AGREEMENT_SELECT = `
  id, business_id, customer_id, stock_item_id, order_id, rental_number, quantity, returned_quantity,
  rate_cents, billing_interval, status, cancel_at_period_end, started_at,
  next_bill_date, last_billed_at, last_invoice_id, failed_attempts,
  ended_at, end_reason, created_at,
  customer:customers ( id, first_name, last_name, email, company ),
  item:stock_items ( id, name, unit, deposit_cents )
`;

export async function listRentalAgreements(businessId: string, customerId?: string): Promise<RentalAgreement[]> {
  let query = supabase
    .from('rental_agreements')
    .select(AGREEMENT_SELECT)
    .eq('business_id', businessId)
    .order('created_at', { ascending: false });
  if (customerId) query = query.eq('customer_id', customerId);
  const { data, error } = await query;
  if (error) throw error;
  return (data || []) as unknown as RentalAgreement[];
}

export async function listRentalEvents(agreementId: string): Promise<RentalEvent[]> {
  const { data, error } = await supabase
    .from('rental_events')
    .select('id, agreement_id, event_type, message, details, actor_id, created_at')
    .eq('agreement_id', agreementId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []) as RentalEvent[];
}

/** Schedule (or revoke) a cancellation at the end of the paid period. */
export async function cancelRentalAgreement(agreementId: string, cancel: boolean): Promise<void> {
  const { error } = await supabase.rpc('cancel_rental_agreement', {
    p_agreement_id: agreementId,
    p_cancel: cancel,
  });
  if (error) throw error;
}

/** Record units coming back; ends the agreement when everything is back
 *  (or endAll writes off whatever is still out). */
export async function endRentalAgreement(args: {
  agreementId: string;
  returned?: number;
  reason?: string;
  endAll?: boolean;
}): Promise<{ status: string; returned: number; still_out: number }> {
  const { data, error } = await supabase.rpc('end_rental_agreement', {
    p_agreement_id: args.agreementId,
    p_returned: args.returned ?? null,
    p_reason: args.reason ?? null,
    p_end_all: args.endAll ?? false,
  });
  if (error) throw error;
  return data as { status: string; returned: number; still_out: number };
}

export type RentalStartMode = 'card_now' | 'link' | 'silent';

export interface RentalStartResult {
  agreement_id: string;
  rental_number?: string | null;
  /** null when billing is deferred — the first invoice generates on first_bill_date. */
  invoice_id: string | null;
  invoice_number: string | null;
  total_cents: number;
  wallet_applied_cents: number;
  remaining_cents: number;
  customer_email: string | null;
  status: 'pending' | 'active';
  first_bill_date?: string;
}

/** Staff-side rental start. card_now → agreement stays pending until its
 *  invoice is paid (activation trigger); link/silent → active immediately.
 *  firstBillDate (future) → active now, no invoice today: the daily biller
 *  generates the first invoice (period + deposit) on that date. */
export async function startRentalAgreement(args: {
  businessId: string;
  customerId: string;
  stockItemId: string;
  quantity: number;
  rateCents?: number | null;
  mode: RentalStartMode;
  /** YYYY-MM-DD; must be a future date to defer billing. */
  firstBillDate?: string | null;
}): Promise<RentalStartResult> {
  const { data, error } = await supabase.rpc('start_rental_agreement', {
    p_business_id: args.businessId,
    p_customer_id: args.customerId,
    p_stock_item_id: args.stockItemId,
    p_quantity: args.quantity,
    p_rate_cents: args.rateCents ?? null,
    p_mode: args.mode,
    p_first_bill_date: args.firstBillDate ?? null,
  });
  if (error) throw error;
  return data as RentalStartResult;
}

/** One invoice that billed a rental agreement (via its tagged line items). */
export interface RentalInvoiceRef {
  invoice_id: string;
  invoice_number: string;
  status: 'draft' | 'open' | 'partially_paid' | 'paid' | 'void';
  total_cents: number;
  amount_paid_cents: number;
  amount_due_cents: number;
  date: string | null;
}

/** Every invoice that ever billed this agreement, newest first. */
export async function listRentalInvoices(agreementId: string): Promise<RentalInvoiceRef[]> {
  const { data, error } = await supabase
    .from('invoice_line_items')
    .select('invoice_id, invoice:invoices ( id, invoice_number, status, total_cents, amount_paid_cents, amount_due_cents, finalized_at, created_at )')
    .eq('rental_agreement_id', agreementId);
  if (error) throw error;
  const byId = new Map<string, RentalInvoiceRef>();
  for (const row of (data || []) as Array<{ invoice: Record<string, unknown> | null }>) {
    const inv = row.invoice;
    if (!inv || byId.has(inv.id as string)) continue;
    byId.set(inv.id as string, {
      invoice_id: inv.id as string,
      invoice_number: String(inv.invoice_number ?? ''),
      status: inv.status as RentalInvoiceRef['status'],
      total_cents: Number(inv.total_cents) || 0,
      amount_paid_cents: Number(inv.amount_paid_cents) || 0,
      amount_due_cents: Number(inv.amount_due_cents) || 0,
      date: (inv.finalized_at as string | null) || (inv.created_at as string | null),
    });
  }
  return [...byId.values()].sort((a, b) => (b.date || '').localeCompare(a.date || ''));
}

/** invoice_id → rental numbers billed on it (for the Invoices page badge/search). */
export async function fetchRentalInvoiceMap(businessId: string): Promise<Map<string, string[]>> {
  const { data, error } = await supabase
    .from('invoice_line_items')
    .select('invoice_id, rental:rental_agreements ( rental_number )')
    .eq('business_id', businessId)
    .not('rental_agreement_id', 'is', null);
  if (error) throw error;
  const map = new Map<string, string[]>();
  for (const row of (data || []) as Array<{ invoice_id: string; rental: { rental_number: string | null } | null }>) {
    const num = row.rental?.rental_number;
    if (!num) continue;
    const list = map.get(row.invoice_id) || [];
    if (!list.includes(num)) list.push(num);
    map.set(row.invoice_id, list);
  }
  return map;
}

/** Email the pay link for a rental invoice (paying it saves a card on file). */
export async function sendRentalPayLink(businessId: string, invoiceId: string): Promise<{ sent: boolean; to: string }> {
  const { data, error } = await supabase.functions.invoke('rental-billing', {
    body: { businessId, action: 'send_pay_link', invoiceId },
  });
  if (error) throw error;
  if (data?.error) throw new Error(String(data.error));
  return data as { sent: boolean; to: string };
}

/** Cancel a staff-started rental that never got paid (voids its invoice). */
export async function cancelPendingRental(agreementId: string): Promise<void> {
  const { error } = await supabase.rpc('cancel_pending_rental', { p_agreement_id: agreementId });
  if (error) throw error;
}

/** Which of these customers have a saved card on file. */
export async function fetchCustomersWithCards(businessId: string, customerIds: string[]): Promise<Set<string>> {
  const ids = Array.from(new Set(customerIds.filter(Boolean)));
  if (ids.length === 0) return new Set();
  const { data, error } = await supabase
    .from('customer_saved_cards')
    .select('customer_id')
    .eq('business_id', businessId)
    .in('customer_id', ids);
  if (error) throw error;
  return new Set((data || []).map((r) => r.customer_id as string));
}

/** Manually run rental billing (whole business, or one customer). */
export async function runRentalBilling(businessId: string, customerId?: string): Promise<{
  processed: number;
  outcomes: Array<{ result: string; invoiceNumber?: number | null; detail?: string }>;
}> {
  const { data, error } = await supabase.functions.invoke('rental-billing', {
    body: { businessId, ...(customerId ? { customerId } : {}) },
  });
  if (error) throw error;
  return data as { processed: number; outcomes: Array<{ result: string; invoiceNumber?: number | null; detail?: string }> };
}

export function rentalRateText(a: Pick<RentalAgreement, 'rate_cents' | 'billing_interval' | 'quantity'>): string {
  const per = `$${(a.rate_cents / 100).toFixed(2)}/${a.billing_interval === 'week' ? 'wk' : 'mo'}`;
  return a.quantity > 1 ? `${a.quantity} × ${per}` : per;
}

export const RENTAL_STATUS_LABEL: Record<RentalAgreement['status'], string> = {
  pending: 'Pending delivery',
  active: 'Active',
  past_due: 'Past due',
  canceled: 'Canceled',
  ended: 'Ended',
};

export const RENTAL_STATUS_CLASSES: Record<RentalAgreement['status'], string> = {
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  past_due: 'bg-red-50 text-red-700 border-red-200',
  canceled: 'bg-gray-100 text-gray-600 border-gray-200',
  ended: 'bg-gray-100 text-gray-600 border-gray-200',
};
